import { useEffect, useMemo, useRef } from "react";
import { useController, type FieldPath, type FieldValues } from "react-hook-form";
import { useDispatch } from "react-redux";

import {
  setMotorSumInsuredLoading,
  setMotorSumInsuredSuccess,
} from "@/D2C/features/car-quote/redux/motor-suminsured/motorSumInsuredSlice";
import {
  getMotorSumInsuredRange,
  normalizeMotorSumInsuredResponse,
} from "@/D2C/features/car-quote/utils/motorSumInsured";
import { CurrencyInput } from "@/shared/components/CurrencyInput";
import { LOCALISATION_CONFIG, LOCALISATION_PROFILE } from "@/shared/config/localisation";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import type { AppDispatch } from "@/shared/redux/store/store";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import type { BaseInputProps } from "@/shared/types";
import { formatAmountByConfig } from "../utils/amountFormat";

type MotorSumInsuredInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  apiEndpoint: string;
  isCallingApi?: boolean;
  showAmountInWords?: boolean;
};

export function MotorMinMaxInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder = LOCALISATION_PROFILE.motor.vehicleValuePlaceholder,
  disabled,
  required,
  className,
  apiEndpoint,
  isCallingApi = false,
  showAmountInWords = true,
}: MotorSumInsuredInputProps<TFieldValues, TName>) {
  const dispatch = useDispatch<AppDispatch>();
  const currencyCode = LOCALISATION_CONFIG.currencyCode;
  const { motorSumInsured: motorSumInsuredState } = usePosterReducers();
  const { field } = useController({ control, name });
  const calledKeyRef = useRef<string | null>(null);
  const defaultSetRef = useRef(false);

  // ── Derived ───────────────────────────────────────────────────────────────
  const range = useMemo(
    () => getMotorSumInsuredRange(motorSumInsuredState.data),
    [motorSumInsuredState.data]
  );

  const description = useMemo(() => {
    if (range) {
      const base = `From ${currencyCode} ${formatAmountByConfig(range.min)} to ${currencyCode} ${formatAmountByConfig(range.max)}`;
      return motorSumInsuredState.source === "fallback"
        ? `${base} (demo data until API is live)`
        : base;
    }
    if (motorSumInsuredState.status === "loading") return "Loading insured value range...";
    return motorSumInsuredState.error ?? undefined;
  }, [
    currencyCode,
    range,
    motorSumInsuredState.source,
    motorSumInsuredState.status,
    motorSumInsuredState.error,
  ]);

  // ── Fetch once per endpoint ───────────────────────────────────────────────
  useEffect(() => {
    if (!isCallingApi) return;
    if (calledKeyRef.current === apiEndpoint) return;
    if (motorSumInsuredState.status === "loading" || motorSumInsuredState.status === "succeeded") {
      calledKeyRef.current = apiEndpoint;
      return;
    }

    calledKeyRef.current = apiEndpoint;

    let isMounted = true;

    const fetchRange = async () => {
      dispatch(setMotorSumInsuredLoading());
      try {
        const response = await axiosInstance.get(apiEndpoint);

        const payload =
          response?.data?.data &&
          typeof response.data.data === "object" &&
          !Array.isArray(response.data.data)
            ? response.data.data
            : response.data;

        const normalized = normalizeMotorSumInsuredResponse(payload);

        if (!Object.keys(normalized).length) {
          throw new Error("Empty insured value range from API.");
        }

        if (isMounted) {
          dispatch(setMotorSumInsuredSuccess({ data: normalized, source: "api" }));
        }
      } catch (err) {
        console.error("MotorMinMaxInput fetch error:", err);
      }
    };

    void fetchRange();
    return () => {
      isMounted = false;
    };
  }, [isCallingApi, apiEndpoint]);

  // ── Set Medium as default value (once, when API data arrives) ────────────
  useEffect(() => {
    const medium = motorSumInsuredState.data?.Medium;

    // Guard: no data, or already set, or user already typed something
    if (!medium || defaultSetRef.current || field.value) return;

    defaultSetRef.current = true;
    field.onChange(String(medium)); // ✅ field is accessible here via useController
  }, [motorSumInsuredState.data?.Medium]); // field.onChange is stable — safe to omit

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <CurrencyInput
      control={control}
      name={name}
      label={label}
      description={description}
      required={required}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      currencyCode={currencyCode}
      formatValueOnBlur={formatAmountByConfig}
      showAmountInWords={showAmountInWords}
    />
  );
}
